import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiClock, FiMonitor, FiSmartphone, FiTablet, FiGlobe, FiMousePointer,
  FiChevronDown, FiChevronRight, FiCheckCircle,
  FiRefreshCw, FiCalendar, FiUsers, FiTrendingUp, FiMap, FiInfo
} from 'react-icons/fi';
import { getSessionHistory } from '../../services/api';

// Country flag emoji helper
const getCountryFlag = (countryCode) => {
  if (!countryCode || countryCode.length !== 2) return '🌍';
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt());
  return String.fromCodePoint(...codePoints);
};

// Device icon helper
const DeviceIcon = ({ type }) => {
  const deviceType = type?.toLowerCase() || '';
  if (deviceType === 'mobile') return <FiSmartphone className="w-4 h-4" />;
  if (deviceType === 'tablet') return <FiTablet className="w-4 h-4" />;
  return <FiMonitor className="w-4 h-4" />;
};

// Source badge helper with enhanced categories (tooltip moved to header)
const SourceBadge = ({ source, sourceDetail }) => {
  const colors = {
    'Google': 'bg-blue-100 text-blue-700',
    'Bing': 'bg-teal-100 text-teal-700',
    'Yahoo': 'bg-purple-100 text-purple-700',
    'DuckDuckGo': 'bg-orange-100 text-orange-700',
    'LinkedIn': 'bg-sky-100 text-sky-700',
    'Twitter/X': 'bg-gray-100 text-gray-700',
    'Facebook': 'bg-indigo-100 text-indigo-700',
    'Instagram': 'bg-pink-100 text-pink-700',
    'YouTube': 'bg-red-100 text-red-700',
    'Pinterest': 'bg-rose-100 text-rose-700',
    'Reddit': 'bg-orange-100 text-orange-700',
    'WhatsApp': 'bg-green-100 text-green-700',
    'Telegram': 'bg-cyan-100 text-cyan-700',
    'Email': 'bg-amber-100 text-amber-700',
    'Referral': 'bg-purple-100 text-purple-700',
    'Direct': 'bg-green-100 text-green-700',
    'Bot/Crawler': 'bg-gray-200 text-gray-500',
    'Preview/Test': 'bg-yellow-100 text-yellow-600'
  };
  
  // Source icons
  const icons = {
    'Google': '🔍',
    'Bing': '🔎',
    'LinkedIn': '💼',
    'Twitter/X': '𝕏',
    'Facebook': '📘',
    'Instagram': '📸',
    'YouTube': '▶️',
    'Email': '📧',
    'Direct': '🔗',
    'Bot/Crawler': '🤖',
    'Preview/Test': '🧪'
  };
  
  const icon = icons[source] || '🌐';
  const colorClass = colors[source] || (source?.startsWith('Campaign:') ? 'bg-violet-100 text-violet-700' : 'bg-gray-100 text-gray-600');
  
  return (
    <div className="flex flex-col">
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${colorClass} inline-flex items-center gap-1`}>
        <span>{icon}</span>
        {source}
      </span>
      {sourceDetail && (
        <span className="text-xs text-gray-400 mt-0.5 truncate max-w-[120px]" title={sourceDetail}>
          {sourceDetail}
        </span>
      )}
    </div>
  );
};

// Header tooltip component - position can be 'left' (default), 'right', or 'top'
const HeaderTooltip = ({ content, position = 'left' }) => {
  const [isVisible, setIsVisible] = useState(false);
  
  // Determine horizontal alignment
  const horizontalClasses = position === 'right' 
    ? 'right-0' 
    : 'left-0';
  
  // Determine vertical position - 'top' makes tooltip appear above the icon
  const isTopPosition = position === 'top';
  const verticalClasses = isTopPosition ? 'bottom-6' : 'top-6';
  
  // Arrow position changes based on vertical position
  const arrowClasses = isTopPosition
    ? 'left-2 -bottom-1.5'  // Arrow at bottom pointing down
    : (position === 'right' ? 'right-2 -top-1.5' : 'left-2 -top-1.5');  // Arrow at top pointing up
  
  return (
    <div className="relative inline-block ml-1">
      <FiInfo 
        className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 cursor-help" 
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
      />
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ opacity: 0, y: isTopPosition ? -5 : 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: isTopPosition ? -5 : 5 }}
            transition={{ duration: 0.15 }}
            className={`absolute z-50 ${horizontalClasses} ${verticalClasses} w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-xl`}
          >
            {content}
            <div className={`absolute ${arrowClasses} w-3 h-3 bg-gray-900 transform rotate-45`}></div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Source categories tooltip content
const SourceTooltipContent = () => (
  <div className="space-y-2">
    <p className="font-semibold mb-2">Traffic Source Categories:</p>
    <div className="space-y-1.5">
      <p><span className="text-green-400">🔗 Direct</span> – Typed URL or bookmark</p>
      <p><span className="text-blue-400">🔍 Google/Bing</span> – Search engines</p>
      <p><span className="text-sky-400">💼 LinkedIn/𝕏</span> – Social media</p>
      <p><span className="text-violet-400">📊 Campaign</span> – UTM tagged links</p>
      <p><span className="text-amber-400">📧 Email</span> – Email referrals</p>
      <p><span className="text-gray-400">🤖 Bot/Crawler</span> – Automated traffic</p>
      <p><span className="text-yellow-400">🧪 Preview/Test</span> – Test environments</p>
    </div>
  </div>
);

// Status categories tooltip content
const StatusTooltipContent = () => (
  <div className="space-y-2">
    <p className="font-semibold mb-2">Session Status Categories:</p>
    <div className="space-y-1.5">
      <p><span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-1.5"></span><span className="text-green-400">Live</span> – Currently browsing</p>
      <p><span className="inline-block w-2 h-2 rounded-full bg-gray-400 mr-1.5"></span><span className="text-gray-400">Complete</span> – Session ended</p>
      <p><span className="text-emerald-400">🔥 High</span> – 2+ min, viewed multiple sections</p>
      <p><span className="text-amber-400">Medium</span> – 30s-2 min engagement</p>
      <p><span className="text-gray-400">Low</span> – Under 30s engagement</p>
      <p><span className="text-red-400">Bounce</span> – Left without interaction</p>
      <p><span className="text-green-400">✓ Converted</span> – Submitted inquiry</p>
    </div>
  </div>
);

// Conversion Status tooltip content
const ConversionTooltipContent = () => (
  <div className="space-y-2">
    <p className="font-semibold mb-2">What is Conversion?</p>
    <p className="text-gray-300 mb-3">A conversion occurs when a visitor takes a desired action, such as submitting an inquiry or contact form.</p>
    <p className="font-semibold mb-2">Conversion Types:</p>
    <div className="space-y-1.5">
      <p><span className="text-green-400">✓ Request Quote</span> – Submitted a quote request</p>
      <p><span className="text-green-400">✓ Technical Discussion</span> – Requested technical consultation</p>
      <p><span className="text-green-400">✓ Factory Visit</span> – Scheduled a factory visit</p>
      <p><span className="text-green-400">✓ Upload RFQ</span> – Uploaded an RFQ document</p>
      <p><span className="text-green-400">✓ General Inquiry</span> – Submitted general inquiry</p>
      <p><span className="text-gray-400">No Conversion</span> – Visitor left without inquiry</p>
    </div>
  </div>
);

// Engagement badge with live indicator dot
const EngagementBadge = ({ level, converted, isBounce, isLive }) => {
  const colors = {
    'high': 'bg-emerald-100 text-emerald-700',
    'medium': 'bg-amber-100 text-amber-700',
    'low': 'bg-gray-100 text-gray-500'
  };
  
  // Get the status badge
  const getStatusBadge = () => {
    if (converted) {
      return (
        <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 flex items-center gap-1">
          <FiCheckCircle className="w-3 h-3" /> Converted
        </span>
      );
    }
    if (isBounce) {
      return (
        <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-600">
          Bounce
        </span>
      );
    }
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[level] || 'bg-gray-100'}`}>
        {level === 'high' ? '🔥 High' : level === 'medium' ? 'Medium' : 'Low'}
      </span>
    );
  };
  
  return (
    <div className="flex items-center gap-2">
      {/* Live indicator dot - always visible */}
      <div className="relative" title={isLive ? 'Currently browsing' : 'Session ended'}>
        <span className={`w-3 h-3 rounded-full inline-block ${
          isLive 
            ? 'bg-green-500 animate-pulse' 
            : 'bg-gray-300'
        }`}></span>
        {isLive && (
          <span className="absolute inset-0 w-3 h-3 rounded-full bg-green-500 animate-ping opacity-75"></span>
        )}
      </div>
      {/* Status badge */}
      {getStatusBadge()}
    </div>
  );
};

// Session row component
const SessionRow = ({ session, index, isExpanded, onToggle }) => {
  const sectionLabels = {
    'hero': 'Hero Section',
    'solutions': 'Solutions',
    'approach': 'Approach',
    'expertise': 'Expertise',
    'about': 'About Us',
    'contact': 'Contact',
    'domains': 'Strategic Sectors',
    'products': 'Core Products',
    'proof': 'Why Partner',
    'footer': 'Footer'
  };

  return (
    <>
      <tr 
        className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors ${
          session.is_bot ? 'bg-gray-50/50 opacity-60' : 
          session.is_preview ? 'bg-yellow-50/30' :
          session.converted ? 'bg-green-50/30' : 
          session.is_bounce ? 'bg-red-50/20' : ''
        }`}
        onClick={onToggle}
      >
        <td className="px-4 py-3 text-sm text-gray-500">{index}</td>
        <td className="px-4 py-3">
          <div className="text-sm font-medium text-gray-900">{session.start_time_ist}</div>
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="text-lg">{getCountryFlag(session.country_code)}</span>
            <div>
              <div className="text-sm font-medium text-gray-900">{session.city}</div>
              <div className="text-xs text-gray-500">{session.country}</div>
            </div>
          </div>
        </td>
        <td className="px-4 py-3">
          <code className="text-sm text-gray-700 bg-gray-100 px-2 py-1 rounded font-mono">
            {session.ip_address || 'N/A'}
          </code>
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <DeviceIcon type={session.device_type} />
            <div>
              <div className="text-sm text-gray-900">{session.device_type}</div>
              <div className="text-xs text-gray-500">{session.browser}</div>
            </div>
          </div>
        </td>
        <td className="px-4 py-3">
          <SourceBadge 
            source={session.source} 
            sourceDetail={session.source_detail}
            isBot={session.is_bot}
            isPreview={session.is_preview}
          />
        </td>
        <td className="px-4 py-3">
          <div className="text-sm font-medium text-gray-900">{session.duration_formatted}</div>
        </td>
        <td className="px-4 py-3">
          <EngagementBadge 
            level={session.engagement_level} 
            converted={session.converted}
            isBounce={session.is_bounce}
            isLive={session.is_live}
          />
        </td>
        <td className="px-4 py-3 text-center">
          {isExpanded ? (
            <FiChevronDown className="w-5 h-5 text-gray-400" />
          ) : (
            <FiChevronRight className="w-5 h-5 text-gray-400" />
          )}
        </td>
      </tr>
      
      <AnimatePresence>
        {isExpanded && (
          <tr>
            <td colSpan="9" className="p-0">
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="bg-gray-50 p-6 border-b-2 border-blue-100">
                  {/* Bot/Preview Warning Banner */}
                  {(session.is_bot || session.is_preview) && (
                    <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
                      session.is_bot ? 'bg-gray-200 text-gray-600' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      <span className="text-lg">{session.is_bot ? '🤖' : '🧪'}</span>
                      <span className="text-sm font-medium">
                        {session.is_bot ? 'This session is from a bot or crawler - data may not represent real user behavior' : 
                         'This session is from preview/test environment'}
                      </span>
                    </div>
                  )}
                  
                  {/* UTM Campaign Info */}
                  {(session.utm_source || session.utm_medium || session.utm_campaign) && (
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        📊 Campaign Tracking (UTM)
                      </h4>
                      <div className="grid grid-cols-3 gap-4 bg-violet-50 p-4 rounded-lg border border-violet-200">
                        <div>
                          <span className="text-xs text-violet-600">Source</span>
                          <p className="text-sm font-medium text-violet-900">{session.utm_source || '-'}</p>
                        </div>
                        <div>
                          <span className="text-xs text-violet-600">Medium</span>
                          <p className="text-sm font-medium text-violet-900">{session.utm_medium || '-'}</p>
                        </div>
                        <div>
                          <span className="text-xs text-violet-600">Campaign</span>
                          <p className="text-sm font-medium text-violet-900">{session.utm_campaign || '-'}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Visitor Info */}
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <FiUsers className="w-4 h-4" /> Visitor Info
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-white p-4 rounded-lg border border-gray-200">
                      <div>
                        <span className="text-xs text-gray-500">Browser</span>
                        <p className="text-sm font-medium">{session.browser} {session.browser_version}</p>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500">Operating System</span>
                        <p className="text-sm font-medium">{session.os}</p>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500">Screen Size</span>
                        <p className="text-sm font-medium">{session.screen_size}</p>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500">Session ID</span>
                        <p className="text-sm font-medium font-mono">{session.session_id}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Page Journey */}
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <FiMap className="w-4 h-4" /> Page Journey
                    </h4>
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      {session.page_journey && session.page_journey.length > 0 ? (
                        <div className="flex flex-wrap items-center gap-2">
                          {session.page_journey.map((page, idx) => (
                            <React.Fragment key={idx}>
                              <div className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 rounded-full">
                                <span className="text-xs text-blue-600 font-medium">{idx + 1}.</span>
                                <span className="text-sm text-blue-800">{page.title || page.path}</span>
                              </div>
                              {idx < session.page_journey.length - 1 && (
                                <FiChevronRight className="w-4 h-4 text-gray-300" />
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">No page journey data available</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Time Spent by Section */}
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <FiClock className="w-4 h-4" /> Time Spent by Section
                    </h4>
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      {session.section_time && Object.keys(session.section_time).length > 0 ? (
                        <div className="space-y-3">
                          {Object.entries(session.section_time)
                            .sort((a, b) => b[1] - a[1])
                            .map(([section, time]) => {
                              const maxTime = Math.max(...Object.values(session.section_time));
                              const percentage = (time / maxTime) * 100;
                              const isHot = percentage > 80;
                              return (
                                <div key={section} className="flex items-center gap-3">
                                  <span className="text-sm text-gray-600 w-32 truncate">
                                    {sectionLabels[section] || section}
                                  </span>
                                  <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                                    <div 
                                      className={`h-full rounded-full transition-all ${
                                        isHot ? 'bg-orange-500' : 'bg-blue-500'
                                      }`}
                                      style={{ width: `${percentage}%` }}
                                    />
                                  </div>
                                  <span className="text-sm font-medium text-gray-700 w-16 text-right">
                                    {time >= 60 ? `${Math.floor(time / 60)}m ${Math.round(time % 60)}s` : `${Math.round(time)}s`}
                                  </span>
                                  {isHot && <span className="text-orange-500">🔥</span>}
                                </div>
                              );
                            })}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">No section time data available</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Click Activity */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <FiMousePointer className="w-4 h-4" /> Click Activity
                      </h4>
                      <div className="bg-white p-4 rounded-lg border border-gray-200">
                        <div className="text-3xl font-bold text-gray-900">{session.clicks}</div>
                        <p className="text-sm text-gray-500">Total Clicks</p>
                      </div>
                    </div>
                    
                    {/* Conversion Status */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <FiTrendingUp className="w-4 h-4" /> Conversion Status
                        <HeaderTooltip content={<ConversionTooltipContent />} position="top" />
                      </h4>
                      <div className={`p-4 rounded-lg border ${
                        session.converted 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-white border-gray-200'
                      }`}>
                        {session.converted ? (
                          <div className="flex items-center gap-3">
                            <FiCheckCircle className="w-8 h-8 text-green-500" />
                            <div>
                              <p className="text-sm font-semibold text-green-700">Inquiry Submitted</p>
                              <p className="text-xs text-green-600">{session.conversion_type}</p>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">No conversion</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </td>
          </tr>
        )}
      </AnimatePresence>
    </>
  );
};

// Date group component
const DateGroup = ({ dateData, sessions, startIndex, expandedSessions, onToggleSession, isCollapsed, onToggleCollapse }) => {
  return (
    <div className="mb-6">
      {/* Date Header */}
      <div 
        className="flex items-center justify-between bg-gray-100 px-4 py-3 rounded-t-lg cursor-pointer hover:bg-gray-200 transition-colors"
        onClick={onToggleCollapse}
      >
        <div className="flex items-center gap-3">
          {isCollapsed ? (
            <FiChevronRight className="w-5 h-5 text-gray-500" />
          ) : (
            <FiChevronDown className="w-5 h-5 text-gray-500" />
          )}
          <FiCalendar className="w-4 h-4 text-gray-500" />
          <span className="font-semibold text-gray-800">{dateData.date_formatted}</span>
          <span className="text-sm text-gray-500">({dateData.session_count} sessions)</span>
        </div>
      </div>
      
      {/* Sessions Table */}
      <AnimatePresence>
        {!isCollapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="overflow-x-auto border border-t-0 border-gray-200 rounded-b-lg">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">#</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP Address</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Device</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="flex items-center">
                        Source
                        <HeaderTooltip content={<SourceTooltipContent />} />
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="flex items-center">
                        Status
                        <HeaderTooltip content={<StatusTooltipContent />} position="right" />
                      </div>
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((session, idx) => (
                    <SessionRow
                      key={session.session_id}
                      session={session}
                      index={startIndex + idx + 1}
                      isExpanded={expandedSessions.has(session.session_id)}
                      onToggle={() => onToggleSession(session.session_id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

function SessionHistory() {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState('today');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [expandedSessions, setExpandedSessions] = useState(new Set());
  const [collapsedDates, setCollapsedDates] = useState(new Set());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  
  // Custom date range state
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const fetchData = useCallback(async (showRefreshIndicator = false) => {
    if (showRefreshIndicator) setIsRefreshing(true);
    try {
      const params = { 
        dateRange, 
        page, 
        limit: limit === 0 ? 1000 : limit 
      };
      
      // Add custom date params if custom range is selected
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        params.startDate = customStartDate;
        params.endDate = customEndDate;
      }
      
      const response = await getSessionHistory(params);
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch session history:', error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [dateRange, page, limit, customStartDate, customEndDate]);

  useEffect(() => {
    setIsLoading(true);
    setPage(1);
    fetchData();
  }, [dateRange, limit]);

  useEffect(() => {
    fetchData();
  }, [page, fetchData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      fetchData(true);
    }, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);

  const toggleSession = (sessionId) => {
    setExpandedSessions(prev => {
      const next = new Set(prev);
      if (next.has(sessionId)) {
        next.delete(sessionId);
      } else {
        next.add(sessionId);
      }
      return next;
    });
  };

  const toggleDateCollapse = (date) => {
    setCollapsedDates(prev => {
      const next = new Set(prev);
      if (next.has(date)) {
        next.delete(date);
      } else {
        next.add(date);
      }
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const summary = data?.summary || {};
  const sessionsByDate = data?.sessions_by_date || [];
  const pagination = data?.pagination || {};

  // Calculate running index for serial numbers
  let runningIndex = (pagination.current_page - 1) * pagination.items_per_page;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Sessions</p>
              <p className="text-2xl font-bold text-gray-900">{summary.total_sessions || 0}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <FiUsers className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Avg Duration</p>
              <p className="text-2xl font-bold text-gray-900">{summary.avg_duration_formatted || '0s'}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <FiClock className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Device Split</p>
              <p className="text-lg font-bold text-gray-900">
                📱 {summary.device_breakdown?.mobile_percent || 0}% / 💻 {summary.device_breakdown?.desktop_percent || 0}%
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <FiMonitor className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Countries</p>
              <p className="text-2xl font-bold text-gray-900">{summary.countries_count || 0}</p>
            </div>
            <div className="p-3 bg-amber-100 rounded-lg">
              <FiGlobe className="w-6 h-6 text-amber-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-500 flex items-center gap-1">
              <FiCalendar className="w-4 h-4" /> Time Range:
            </span>
            {['today', 'yesterday', 'this_week', 'this_month', 'last_month', 'all'].map((range) => (
              <button
                key={range}
                onClick={() => {
                  setDateRange(range);
                  setShowCustomDatePicker(false);
                }}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  dateRange === range && !showCustomDatePicker
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {range === 'all' ? 'All' : range.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </button>
            ))}
            {/* Custom Date Range Button */}
            <button
              onClick={() => {
                setShowCustomDatePicker(!showCustomDatePicker);
                if (!showCustomDatePicker) {
                  // Set default dates when opening (last 7 days)
                  const today = new Date();
                  const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
                  setCustomEndDate(today.toISOString().split('T')[0]);
                  setCustomStartDate(weekAgo.toISOString().split('T')[0]);
                }
              }}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${
                dateRange === 'custom' || showCustomDatePicker
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <FiCalendar className="w-4 h-4" />
              Custom
            </button>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Pagination limit */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Show:</span>
              <select
                value={limit}
                onChange={(e) => setLimit(parseInt(e.target.value))}
                className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={0}>All</option>
              </select>
            </div>
            
            {/* Auto-refresh toggle */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-600">Auto-refresh</span>
            </label>
            
            {/* Manual refresh */}
            <button
              onClick={() => fetchData(true)}
              disabled={isRefreshing}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              <FiRefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
        
        {/* Custom Date Picker Panel */}
        <AnimatePresence>
          {showCustomDatePicker && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex flex-wrap items-end gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      className="px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                    <input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      className="px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <button
                    onClick={() => {
                      if (customStartDate && customEndDate) {
                        setDateRange('custom');
                        setShowCustomDatePicker(false);
                      }
                    }}
                    disabled={!customStartDate || !customEndDate}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Apply
                  </button>
                  <button
                    onClick={() => {
                      setShowCustomDatePicker(false);
                      setCustomStartDate('');
                      setCustomEndDate('');
                    }}
                    className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
                {dateRange === 'custom' && customStartDate && customEndDate && (
                  <p className="mt-2 text-sm text-blue-600">
                    Showing sessions from {customStartDate} to {customEndDate}
                  </p>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Last updated */}
        <div className="mt-3 text-xs text-gray-400">
          Last updated: {data?.timestamp_ist || 'N/A'}
        </div>
      </div>

      {/* Session List */}
      <div className="space-y-4">
        {sessionsByDate.length > 0 ? (
          sessionsByDate.map((dateData) => {
            const dateGroup = (
              <DateGroup
                key={dateData.date}
                dateData={dateData}
                sessions={dateData.sessions}
                startIndex={runningIndex}
                expandedSessions={expandedSessions}
                onToggleSession={toggleSession}
                isCollapsed={collapsedDates.has(dateData.date)}
                onToggleCollapse={() => toggleDateCollapse(dateData.date)}
              />
            );
            runningIndex += dateData.sessions.length;
            return dateGroup;
          })
        ) : (
          <div className="bg-white rounded-xl p-12 border border-gray-200 text-center">
            <FiUsers className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No sessions found for this time period</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="flex items-center justify-between bg-white rounded-xl p-4 border border-gray-200">
          <p className="text-sm text-gray-500">
            Showing {((pagination.current_page - 1) * pagination.items_per_page) + 1} - {Math.min(pagination.current_page * pagination.items_per_page, pagination.total_items)} of {pagination.total_items} sessions
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={pagination.current_page <= 1}
              className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="px-3 py-1.5 text-sm text-gray-600">
              Page {pagination.current_page} of {pagination.total_pages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(pagination.total_pages, p + 1))}
              disabled={pagination.current_page >= pagination.total_pages}
              className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SessionHistory;
